import * as React from 'react';
import { useState, useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useSharedHistoryContext } from '../context/SharedHistoryContext';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { LayoutPlugin } from '../plugins/LayoutPlugin/LayoutPlugin';
import TableCellResizer from '../plugins/TableCellResizer';
import TableHoverActionsPlugin from '../plugins/TableHoverActionsPlugin';
import TableCellActionMenuPlugin from '../plugins/TableActionMenuPlugin';
import ContextMenuPlugin from '../plugins/ContextMenuPlugin';
import TableOfContentsPlugin from '../plugins/TableOfContentsPlugin';
import { HorizontalRulePlugin } from '@lexical/react/LexicalHorizontalRulePlugin';
import ContentEditable from '../ui/ContentEditable';
import ToolbarPlugin from '../plugins/ToolbarPlugin';
import { BaseEditorPlugins, FloatingPlugins } from '../BaseEditor';
import ListMaxIndentLevelPlugin from '../plugins/ListMaxIndentLevelPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import PreviewArticleComponent from '../ui/PreviewArticleComponent';
import { useEditorState } from '../BaseEditor/hooks/useBaseEditorFormatState';
import { $getRoot } from 'lexical';
import { useSearchParams } from 'react-router-dom';

// Import the story card component and related utilities
import ExternalNewsCard from '../../DiscoveryComponent/MiddleContentComponent/components/external-news-card.component';
import { trendingTopicsApi } from '../../../apis/trending-topics.api';
import { transformTopicToNewsItem } from '../../../utils/news-trending-stories-transformer';

// Import icons
import WriteBlackIcon from '../../../assets/write-black-icon.svg';

import {
  useViewportWidth,
  useFloatingAnchor,
  useEditorStateSync,
  useMediaNodesContributors
} from './hooks';
import { Container } from '@chakra-ui/react';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';

interface ArticleEditorProps {
  editorStateRef: React.MutableRefObject<any>;
  textLengthRef: React.MutableRefObject<number>;
  onChange: () => void;
  forceSave?: () => void;
  triggerSave?: () => void;
  onArticleContentChange?: (content: string) => void;
}

// Custom Large Placeholder Component
const LargePlaceholder = () => (
  <div
    className="absolute pointer-events-none select-none"
    style={{
      top: '10px', // Match ContentEditable padding
      left: '28px', // Match ContentEditable padding
      fontSize: '24px',
      fontWeight: '400',
      color: '#9ca3af',
      zIndex: 1
    }}
  >
    Start writing...
  </div>
);

export default function ArticleEditor({
  editorStateRef,
  textLengthRef,
  onChange,
  forceSave,
  triggerSave,
  onArticleContentChange
}: ArticleEditorProps) {
  const { historyState } = useSharedHistoryContext();
  const [editor] = useLexicalComposerContext();
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);
  const [isEmpty, setIsEmpty] = useState<boolean>(true);
  const [articleContent, setArticleContent] = useState<string>('');
  const { isEditable } = useEditorState(editor as any);
  const [searchParams] = useSearchParams();

  // Story-related state
  const [storyData, setStoryData] = useState(null);
  const [storyLoading, setStoryLoading] = useState(false);
  const [storyError, setStoryError] = useState(null);
  const storyId = searchParams.get('storyId');

  // Custom hooks
  const { isSmallWidthViewport } = useViewportWidth();
  const { floatingAnchorElem, onRef } = useFloatingAnchor();
  const { handleEditorChange } = useMediaNodesContributors();

  // Editor state synchronization
  useEditorStateSync({
    editor,
    editorStateRef,
    textLengthRef,
    onChange
  });

  // Fetch story data when storyId is present
  useEffect(() => {
    if (storyId) {
      const fetchStoryData = async () => {
        try {
          setStoryLoading(true);
          setStoryError(null);

          const result = await trendingTopicsApi.getTrendingTopicById(storyId);

          if (result.success && result.data) {
            // Transform the story data to match the ExternalNewsCard format
            const transformedStory = transformTopicToNewsItem(result.data);
            setStoryData(transformedStory);
          } else {
            setStoryError(result.error || 'Failed to load story data');
            setStoryData(null);
          }
        } catch (error) {
          console.error('Error fetching story data:', error);
          setStoryError('Failed to load story data');
          setStoryData(null);
        } finally {
          setStoryLoading(false);
        }
      };

      fetchStoryData();
    }
  }, [storyId]);

  // Track if editor is empty for placeholder visibility and extract content for AI
  useEffect(() => {
    const removeUpdateListener = editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const root = $getRoot();
        const children = root.getChildren();

        // Extract text content for AI generation
        const textContent = root.getTextContent();
        setArticleContent(textContent);
        
        // Notify parent of content change for sidebar AI features
        if (onArticleContentChange) {
          onArticleContentChange(textContent);
        }

        // Check if editor is empty (no children or only empty paragraphs)
        const hasContent =
          children.length > 0 &&
          children.some((child) => {
            const nodeTextContent = child.getTextContent();
            return nodeTextContent.trim().length > 0;
          });

        setIsEmpty(!hasContent);
      });
    });

    // Initial check
    editor.getEditorState().read(() => {
      const root = $getRoot();
      const children = root.getChildren();

      // Extract initial text content
      const textContent = root.getTextContent();
      setArticleContent(textContent);
      
      // Notify parent of initial content
      if (onArticleContentChange) {
        onArticleContentChange(textContent);
      }

      const hasContent =
        children.length > 0 &&
        children.some((child) => {
          const nodeTextContent = child.getTextContent();
          return nodeTextContent.trim().length > 0;
        });
      setIsEmpty(!hasContent);
    });

    return removeUpdateListener;
  }, [editor, onArticleContentChange]);

  return (
    <div className="w-full">
      <ToolbarPlugin />

      {/* Story Card Section */}
      {storyId && (
        <div className="w-full px-4 md:px-6 py-6 mb-8 rounded-lg">
          <div className="mt-3 w-full flex items-center gap-2 mb-4">
            <img src={WriteBlackIcon} alt="" className="w-6 h-6" />
            <p className="text-lg font-bold text-gray-900">You are sharing your perspective on:</p>
          </div>

          <div className="w-full">
            {storyLoading && (
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            )}

            {storyError && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700">{storyError}</p>
              </div>
            )}

            {storyData && !storyLoading && (
              <ExternalNewsCard item={storyData} hideWriteButton={true} />
            )}
          </div>
        </div>
      )}

      <Container
        className="editor-container !w-full !px-4 md:!px-0 overflow-y-auto !mx-0 !max-w-none"
        // maxW="812px"
        style={
          {
            // height: 'calc(100vh - 69px - 191px)' // Mobile: 100vh - mobile header - stacked footer
            // scrollbarWidth: 'none' // Firefox
            // msOverflowStyle: 'none' // IE and Edge
          }
        }
        sx={{
          '&::-webkit-scrollbar': {
            // display: 'none' // Chrome, Safari, Opera
          }
        }}
      >
        <BaseEditorPlugins
          historyState={historyState}
          maxLength={30000}
          shouldFocus
          isCommentEditor={false}
        />
        {!isEditable && <PreviewArticleComponent />}
        <RichTextPlugin
          contentEditable={
            <div className="editor-scroller max-w-[812px] mx-auto">
              <div className="editor relative" ref={onRef}>
                <ContentEditable placeholder={''} />
                {/* Custom Large Placeholder - only show when editable and empty */}
                {isEditable && isEmpty && <LargePlaceholder />}
              </div>
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
          placeholder={null}
        />
        <OnChangePlugin onChange={handleEditorChange} />
        <TablePlugin hasCellMerge hasCellBackgroundColor hasHorizontalScroll />
        <TableCellResizer />
        <TableHoverActionsPlugin />
        <ListMaxIndentLevelPlugin maxDepth={7} />
        <TabIndentationPlugin />
        <LayoutPlugin />
        <HorizontalRulePlugin />
        <TableOfContentsPlugin />
        <ContextMenuPlugin />
        <AutoFocusPlugin />
        {floatingAnchorElem && !isSmallWidthViewport && (
          <FloatingPlugins
            anchorElem={floatingAnchorElem}
            isLinkEditMode={isLinkEditMode}
            setIsLinkEditMode={setIsLinkEditMode}
          />
        )}
        {floatingAnchorElem && !isSmallWidthViewport && (
          <TableCellActionMenuPlugin anchorElem={floatingAnchorElem} cellMerge={true} />
        )}
      </Container>
    </div>
  );
}
